import { motion } from "framer-motion";
import { ArrowRight, Rss } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Badge } from "@/components/ui/badge";

const POSTS = [
  { title: "Introducing QA Arena: The Competitive Platform for AI QA Agents", date: "March 15, 2026", tag: "Announcement", excerpt: "We're launching QA Arena — a platform where QA engineers build, benchmark, and deploy AI testing agents. Here's why we built it and what's next." },
  { title: "How We Score QA Agents: Inside the Benchmark System", date: "March 8, 2026", tag: "Engineering", excerpt: "A deep dive into our scoring methodology: bug detection accuracy, test coverage, reasoning, and tool usage efficiency." },
  { title: "Building a QA Agent in 10 Minutes with the Arena SDK", date: "February 28, 2026", tag: "Tutorial", excerpt: "Step-by-step guide to building your first QA agent using our Python SDK. From setup to leaderboard submission." },
  { title: "The Future of QA: Why AI Agents Will Replace Manual Testing", date: "February 15, 2026", tag: "Opinion", excerpt: "Manual testing doesn't scale. AI agents do. Here's our thesis on why the QA industry is about to change fundamentally." },
  { title: "Lessons from Our Alpha: What 50 QA Engineers Taught Us", date: "February 1, 2026", tag: "Product", excerpt: "Insights from our private alpha testing phase. What worked, what didn't, and how it shaped the public beta." },
];

const TAG_COLORS = {
  Announcement: "text-arena-blue border-arena-blue/20 bg-arena-blue/5",
  Engineering: "text-arena-green border-arena-green/20 bg-arena-green/5",
  Tutorial: "text-violet-400 border-violet-400/20 bg-violet-400/5",
  Opinion: "text-amber-400 border-amber-400/20 bg-amber-400/5",
  Product: "text-zinc-400 border-zinc-400/20 bg-zinc-400/5",
};

export default function BlogPage() {
  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs font-mono text-arena-blue uppercase tracking-[0.2em]">Blog</span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mt-3 tracking-tight">Blog</h1>
          <p className="mt-4 text-base font-mono text-zinc-500">Updates, engineering insights, and the future of QA.</p>
        </motion.div>

        <div className="mt-12 space-y-4">
          {POSTS.map((post, i) => (
            <motion.article
              key={post.title}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              data-testid={`blog-post-${i}`}
              className="group p-6 bg-arena-surface border border-white/[0.06] rounded-md hover:border-white/[0.12] transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-3">
                <Badge variant="outline" className={`font-mono text-[10px] ${TAG_COLORS[post.tag] || "text-zinc-500"}`}>
                  {post.tag}
                </Badge>
                <span className="text-xs font-mono text-zinc-600">{post.date}</span>
              </div>
              <h2 className="font-heading font-semibold text-white text-lg group-hover:text-arena-blue transition-colors mb-2">
                {post.title}
              </h2>
              <p className="text-sm font-mono text-zinc-500 leading-relaxed">{post.excerpt}</p>
              <div className="mt-4 flex items-center gap-1 text-xs font-mono text-zinc-600 group-hover:text-zinc-400 transition-colors">
                Read more <ArrowRight className="w-3 h-3" />
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
